import { useEffect, useState } from "react";
import { getReq, ApiResponse } from "../utils/request";

/**
 * Dashboard request cache — a dependency-free dedup + TTL layer on top of
 * `getReq` (2026-08-09 perf review).
 *
 * Why: one dashboard tab fires ~30 independent widget GETs with no cache and
 * no in-flight dedup, some for the SAME endpoint (`dashboard/my/kpi` was
 * fetched by both AlertsStrip and the My tab), and every tab switch or
 * remount re-fired all of them. This module gives every dashboard fetch:
 *   - in-flight dedup   → concurrent calls for one URL share one promise;
 *   - a 30s TTL         → within it, cached data is served with NO network call;
 *   - stale-while-revalidate (hook only) → after the TTL the cached data paints
 *     immediately while a background refetch replaces it when it lands.
 *
 * The header Refresh button calls `invalidateDashboardCache()` before bumping
 * its remount tick, so an explicit refresh still forces real fetches.
 *
 * Known gap: the cache is NOT explicitly cleared on logout. Today that is
 * safe because every logout path ends in a hard navigation
 * (`window.location.href = "/login"`), which drops this module-level Map with
 * the rest of the JS heap — but if logout ever becomes an SPA-only route
 * change, wire `invalidateDashboardCache()` into it.
 */

const DEFAULT_TTL_MS = 30 * 1000;

interface CacheEntry {
    /** The (possibly still in-flight) fetch every concurrent caller shares. */
    promise: Promise<ApiResponse<any>>;
    /** Resolved response — null while the fetch is still in flight. */
    response: ApiResponse<any> | null;
    /** When the fetch started; freshness is measured from here. */
    timestamp: number;
}

const cache = new Map<string, CacheEntry>();

// 200 stays cached (the data) and 403 stays cached (the widget hides either
// way, and permissions don't flip mid-session). 5xx / network failures are
// NOT kept — caching a failure would make Retry hand back the same failure
// for the rest of the TTL.
const isCacheableStatus = (status: number): boolean => status === 200 || status === 403;

/**
 * GET through the cache: shares one promise across concurrent callers,
 * serves fresh cached responses without a network call, refetches after the
 * TTL. Drop-in replacement for `getReq` on read-only dashboard endpoints.
 * @param url - The endpoint, exactly as it would be passed to `getReq`.
 * @param ttlMs - Freshness window; defaults to 30s.
 * @returns The same `ApiResponse<T>` shape `getReq` resolves with.
 */
export const cachedGet = <T = any>(url: string, ttlMs: number = DEFAULT_TTL_MS): Promise<ApiResponse<T>> => {
    const existing = cache.get(url);
    if (existing) {
        if (existing.response === null) {
            // In flight — dedup: every concurrent caller awaits the same fetch.
            return existing.promise as Promise<ApiResponse<T>>;
        }
        if (Date.now() - existing.timestamp < ttlMs) {
            // Fresh — no network call.
            return Promise.resolve(existing.response as ApiResponse<T>);
        }
    }

    const entry: CacheEntry = {
        promise: undefined as unknown as Promise<ApiResponse<any>>,
        response: null,
        timestamp: Date.now(),
    };
    entry.promise = getReq<T>(url).then(
        (response) => {
            // Only store if this entry is still the current one — an
            // invalidation while in flight must not resurrect stale data.
            if (cache.get(url) === entry) {
                if (isCacheableStatus(response.status)) {
                    entry.response = response;
                } else {
                    cache.delete(url);
                }
            }
            return response;
        },
        (err) => {
            if (cache.get(url) === entry) {
                cache.delete(url);
            }
            throw err;
        },
    );
    cache.set(url, entry);
    return entry.promise;
};

export interface CachedPeek<T> {
    response: ApiResponse<T>;
    /** Still inside the TTL — safe to show without revalidating. */
    fresh: boolean;
}

/**
 * Synchronously read a cached response without triggering any fetch.
 * @returns The cached response and whether it is still fresh, or null when
 *          nothing (or only an in-flight fetch) is cached for the URL.
 */
export const peekCachedGet = <T = any>(url: string, ttlMs: number = DEFAULT_TTL_MS): CachedPeek<T> | null => {
    const entry = cache.get(url);
    if (!entry || entry.response === null) {
        return null;
    }
    return {
        response: entry.response as ApiResponse<T>,
        fresh: Date.now() - entry.timestamp < ttlMs,
    };
};

/**
 * Drop cached entries so the next call hits the network.
 * @param prefix - Only drop URLs starting with this (e.g. "dashboard/my");
 *                 omit to clear everything (what the header Refresh does).
 */
export const invalidateDashboardCache = (prefix?: string): void => {
    if (!prefix) {
        cache.clear();
        return;
    }
    Array.from(cache.keys()).forEach((key) => {
        if (key.startsWith(prefix)) {
            cache.delete(key);
        }
    });
};

export interface CachedGetState<T> {
    data: T | null;
    loading: boolean;
    /** Network failure or 5xx. */
    error: boolean;
    /** 403 — the BE gate refused this caller. */
    forbidden: boolean;
}

const IDLE_STATE = { data: null, loading: false, error: false, forbidden: false };
const LOADING_STATE = { data: null, loading: true, error: false, forbidden: false };

const stateFromResponse = <T,>(response: ApiResponse<T>): CachedGetState<T> => {
    if (response.status === 200) {
        return { data: response.body, loading: false, error: false, forbidden: false };
    }
    if (response.status === 403) {
        return { data: null, loading: false, error: false, forbidden: true };
    }
    return { data: null, loading: false, error: true, forbidden: false };
};

/**
 * Stateful wrapper over `cachedGet` with stale-while-revalidate:
 *   - cached + fresh → paint immediately, NO loading flash, no network call;
 *   - cached + stale → paint the cached data immediately, refetch in the
 *     background, swap in the result when it lands;
 *   - not cached     → loading state, then fetch (first-load behavior
 *     identical to the old per-widget useEffect fetches).
 * @param url - Endpoint to fetch; pass null to skip fetching entirely.
 * @param tick - Bump to re-run (retry / dashboard-wide refresh counters).
 */
export function useCachedGet<T = any>(url: string | null, tick: number = 0): CachedGetState<T> {
    const [state, setState] = useState<CachedGetState<T>>(() => {
        if (url === null) {
            return IDLE_STATE;
        }
        const cached = peekCachedGet<T>(url);
        return cached ? stateFromResponse(cached.response) : LOADING_STATE;
    });

    useEffect(() => {
        if (url === null) {
            setState((s) => (s.loading ? { ...s, loading: false } : s));
            return;
        }
        let cancelled = false;
        const cached = peekCachedGet<T>(url);
        if (cached) {
            // Cached hit — no loading flash.
            setState(stateFromResponse(cached.response));
            if (cached.fresh) {
                return;
            }
            // Stale: the cached data stays on screen while we revalidate below.
        } else {
            setState(LOADING_STATE);
        }
        (async () => {
            try {
                const response = await cachedGet<T>(url);
                if (!cancelled) {
                    setState(stateFromResponse(response));
                }
            } catch (e) {
                console.error("dashboard widget load failed:", e);
                if (!cancelled) {
                    setState({ data: null, loading: false, error: true, forbidden: false });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [url, tick]);

    return state;
}
